import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { companyApi } from "@/main/api";
import { AutoCompleteSearchControlled } from "@/main/components/shared/autoCompleteSearchControlled/autoCompleteSearchControlled";
import Box from "@/main/components/shared/box";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogKeyboardDatePickerContainer from "@/main/components/shared/datepicker/keyboardDatePickerContainer";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import { KeyValue, KeyValueDefault } from "@/main/models/common";
import type { SearchTermRequest } from "@/main/models/common/searchTermRequest";
import { InsurerFilterRequest } from "@/main/models/request";
import {
	buildCompanyAutocompleteRequest,
	getDefaultVoucherTypeOption,
	getInitialSearchRefundsForm,
	getPendingRefundTypeOption,
	getSingleOption,
	ISearchRefundsForm,
	REFUNDS_RANGE_DAYS,
	searchRefundsSchema,
} from "@/main/screens/order/refunds/refundsScreenUtils";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { VoucherRefundStatusEnum } from "@/main/services/enum/VoucherRefundStatusEnum";
import { VoucherTypeEnum } from "@/main/services/enum/VoucherTypeEnum";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { getKeysIgnoreValidationsDate, onLoadDateRangePicker, validateFormDate } from "@/main/services/utilsDate";
import { userIsBodyshop, userIsSupplier } from "@/main/services/utilsUser";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { useUserReducer } from "@/main/store/user";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { getVoucherRefundStatus, useVoucherRefundStatusReducer } from "@/main/store/voucherRefundStatus";
import { getVoucherType, useVoucherTypeReducer } from "@/main/store/voucherType";
import * as S from "./styles";

const ID_BASE_COMPONENTS = "id-refunds";

type IFiltersRefunds = {
	showFilters: boolean;
	onSubmit: (data: ISearchRefundsForm) => void;
	onClear: () => void;
};

function toLoadedDomain(data: KeyValue[]) {
	return { data, isLoading: false, isError: false, errorMessages: null };
}

function useOwnCompanyDomain(isOwnCompanyOnly: boolean) {
	const { currentUser } = useUserReducer();

	const ownCompanyOptions = useMemo<KeyValue[]>(() => {
		if (!isOwnCompanyOnly || !currentUser) {
			return [];
		}
		return [{ key: currentUser.guidCompany, value: currentUser.companyTradeName }];
	}, [isOwnCompanyOnly, currentUser?.guidCompany, currentUser?.companyTradeName]);

	return toLoadedDomain(ownCompanyOptions);
}

export default function FiltersRefunds({ showFilters, onSubmit, onClear }: Readonly<IFiltersRefunds>) {
	const dispatch = useDispatch();
	const isSupplier = userIsSupplier();
	const isOwnCompanyOnly = isSupplier || userIsBodyshop();
	const [isIgnoreValidationsDate, setIsIgnoreValidationsDate] = useState(false);
	const claimLengthToIgnoreDate = useParameterByTypeScreenReducer(ParameterTypeEnum.ClaimScreenFilterClaimLength);

	const { insurers: insurerReducer } = useInsurerReducer();
	const { voucherType: voucherTypeReducer } = useVoucherTypeReducer();
	const { voucherRefundStatus: refundStatusReducer } = useVoucherRefundStatusReducer();
	const companyDomain = useOwnCompanyDomain(isOwnCompanyOnly);

	const {
		control,
		setValue,
		watch,
		customHandleSubmit,
		reset,
		formState: { errors },
	} = useFormQueryParams<ISearchRefundsForm>({
		mode: "onChange",
		defaultValues: getInitialSearchRefundsForm(),
		onSubmit: handleSubmit,
		resolver: zodResolver(searchRefundsSchema),
		mapDefaultValues: {
			insurer: insurerReducer,
			company: companyDomain,
			refundType: refundStatusReducer,
			voucherType: voucherTypeReducer,
		},
	});

	const insurerKey = String(watch("insurer")?.key ?? "");

	function handleSubmit(formData: ISearchRefundsForm) {
		const ignoreValidationsDateSubmit = (formData.claimNumber?.length ?? 0) >= claimLengthToIgnoreDate;
		const keysIgnoreValidationsDateSubmit = getKeysIgnoreValidationsDate(
			ignoreValidationsDateSubmit,
			formData.startDate,
			formData.finalDate,
		);
		validateFormDate(formData.startDate, formData.finalDate, REFUNDS_RANGE_DAYS, keysIgnoreValidationsDateSubmit);
		onSubmit(formData);
	}

	function checkValidationDates(inputClaimNumber: string) {
		if (inputClaimNumber.length >= claimLengthToIgnoreDate && !isIgnoreValidationsDate) {
			setIsIgnoreValidationsDate(true);
			return true;
		}
		if (inputClaimNumber.length < claimLengthToIgnoreDate && isIgnoreValidationsDate) {
			setIsIgnoreValidationsDate(false);
			return false;
		}
		return null;
	}

	function handleIgnoreValidationsDate() {
		setValue("startDate", null);
		setValue("finalDate", null);
	}

	function handleAcceptDateValidations() {
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(REFUNDS_RANGE_DAYS);
		setValue("startDate", firstDateResult);
		setValue("finalDate", secondDateResult);
	}

	function handleClaimNumberChange(value: string) {
		const isIgnoreValidationDate = checkValidationDates(value);
		if (isNullUndefined(isIgnoreValidationDate)) return;
		if (isIgnoreValidationDate) {
			handleIgnoreValidationsDate();
		} else {
			handleAcceptDateValidations();
		}
	}

	function handleInsurerChange(insurer: KeyValue | KeyValueDefault | null) {
		if (isOwnCompanyOnly || String(insurer?.key ?? "") === insurerKey) {
			return;
		}
		setValue("company", null);
	}

	async function getCompanies(request: SearchTermRequest, signal: AbortSignal) {
		const response = await companyApi.getAutocompleteBodyshopAndSupplier(
			buildCompanyAutocompleteRequest(request, insurerKey),
			signal,
		);
		return response.data.result;
	}

	function handleClear() {
		setIsIgnoreValidationsDate(false);
		reset({
			...getInitialSearchRefundsForm(),
			insurer: getSingleOption(insurerReducer.data),
			company: getSingleOption(companyDomain.data),
			refundType: getPendingRefundTypeOption(refundStatusReducer.data),
			voucherType: getDefaultVoucherTypeOption(voucherTypeReducer.data, isSupplier),
		});
		onClear();
	}

	useEffect(() => {
		const insurerRequest: InsurerFilterRequest = {
			type: HeadQuarterBranchFilterEnum.All,
			idStatusRegister: StatusRegisterEnum.Active,
		};
		dispatch(getInsurers(insurerRequest));

		if (isNullUndefined(voucherTypeReducer.data)) dispatch(getVoucherType());
		if (isNullUndefined(refundStatusReducer.data)) {
			dispatch(getVoucherRefundStatus());
		}

		return () => {
			dispatch(resetReducerInsurers());
		};
	}, []);

	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<form onSubmit={customHandleSubmit} noValidate>
				<S.RowInputs>
					<KogInputTextControlledNew
						id={`${ID_BASE_COMPONENTS}-input-claimNumber`}
						label={getTranslateByKey("information.claimNumber")}
						name="claimNumber"
						control={control}
						onChangeCustom={handleClaimNumberChange}
					/>

					<KogInputTextControlledNew
						id={`${ID_BASE_COMPONENTS}-input-voucherCode`}
						label={getTranslateByKey("information.voucherGridReference")}
						name="voucherCode"
						control={control}
					/>

					<KogKeyboardDatePickerContainer
						idScreen={`${ID_BASE_COMPONENTS}-datePicker-startDate-finalDate`}
						nameStartDate="startDate"
						nameFinalDate="finalDate"
						control={control}
						parameterRangeDays={REFUNDS_RANGE_DAYS}
						ignoreKeyValidations={getKeysIgnoreValidationsDate(
							isIgnoreValidationsDate,
							watch("startDate"),
							watch("finalDate"),
						)}
					/>

					<KogAutoCompleteControlled
						id={`${ID_BASE_COMPONENTS}-select-insurer`}
						label={getTranslateByKey("information.insurer")}
						control={control}
						nameCustom="insurer"
						options={insurerReducer.data}
						isLoading={insurerReducer.isLoading}
						disabled={insurerReducer.data?.length === 1}
						isDefaultValueUnique
						setValueCustom={setValue}
						onChangeCustom={handleInsurerChange}
					/>

					{isOwnCompanyOnly ? (
						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-company`}
							label={getTranslateByKey("information.company")}
							control={control}
							nameCustom="company"
							options={companyDomain.data}
							isLoading={companyDomain.isLoading}
							disabled={companyDomain.data.length === 1}
							isDefaultValueUnique
							setValueCustom={setValue}
							onChangeCustom={() => {}}
						/>
					) : (
						<AutoCompleteSearchControlled
							key={insurerKey}
							id={`${ID_BASE_COMPONENTS}-select-company`}
							name="company"
							control={control}
							label={getTranslateByKey("information.company")}
							loadingText={`${getTranslateByKey("information.searchingForCompanies")}...`}
							getData={getCompanies}
						/>
					)}

					<KogAutoCompleteControlled
						id={`${ID_BASE_COMPONENTS}-select-refundType`}
						label={getTranslateByKey("information.refundType")}
						control={control}
						nameCustom="refundType"
						rulesCustom={{ required: true }}
						hasErrorCustom={!!errors.refundType}
						options={refundStatusReducer.data}
						isLoading={refundStatusReducer.isLoading}
						valueDefaultKey={VoucherRefundStatusEnum.Pending}
						setValueCustom={setValue}
						onChangeCustom={() => {}}
					/>

					<KogAutoCompleteControlled
						id={`${ID_BASE_COMPONENTS}-select-voucherType`}
						label={getTranslateByKey("vouhcer.VoucherType")}
						control={control}
						nameCustom="voucherType"
						options={voucherTypeReducer.data}
						isLoading={voucherTypeReducer.isLoading}
						disabled={isSupplier || voucherTypeReducer.data?.length === 1}
						valueDefaultKey={isSupplier ? VoucherTypeEnum.ReplacementParts : null}
						isDefaultValueUnique
						setValueCustom={setValue}
						onChangeCustom={() => {}}
					/>
				</S.RowInputs>

				<S.AreaButtonFilters>
					<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-search`} type="submit">
						{getTranslateByKey("information.search")}
					</SecondaryButton>

					<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-clear`} onClick={handleClear}>
						{getTranslateByKey("information.clear")}
					</SecondaryButton>
				</S.AreaButtonFilters>
			</form>
		</Box>
	);
}
